.panel {
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    height: var(--tempest-chat-height, 100%);
    background-color: var(--tempest-bg);
    color: var(--tempest-text);
    font-family: var(--tempest-font-sans);
}

.header {
    flex: 0 0 auto;
    padding: var(--tempest-space-3) var(--tempest-space-4);
    border-bottom: 1px solid var(--tempest-border);
}

.thread {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: var(--tempest-space-3);
    min-height: 0;
    padding: var(--tempest-space-4);
    overflow-y: auto;
    overscroll-behavior: contain;
}

.thread:focus-visible {
    outline: 2px solid var(--tempest-primary);
    outline-offset: -2px;
}

.day {
    display: flex;
    align-items: center;
    gap: var(--tempest-space-3);
}

.day::before,
.day::after {
    flex: 1 1 auto;
    height: 1px;
    background-color: var(--tempest-border);
    content: "";
}

.dayLabel {
    color: var(--tempest-text-subtle);
    font-size: var(--tempest-text-xs);
    text-transform: capitalize;
}

.run {
    display: flex;
    gap: var(--tempest-space-3);
    align-items: flex-start;
    max-width: 100%;
}

/*
 * Own messages mirror the row instead of moving to a second column: the avatar,
 * the name and the bubbles keep the same source order, so a screen reader still
 * reads "who, then what" while the eye reads it from the other side.
 */
.ownRun {
    flex-direction: row-reverse;
}

.avatar {
    flex: 0 0 auto;
}

.runBody {
    display: flex;
    flex-direction: column;
    gap: var(--tempest-space-1);
    min-width: 0;
    max-width: min(78%, 42rem);
}

.ownRun .runBody {
    align-items: flex-end;
}

.author {
    color: var(--tempest-text-muted);
    font-size: var(--tempest-text-xs);
    font-weight: var(--tempest-weight-medium);
}

.bubbles {
    display: flex;
    flex-direction: column;
    gap: var(--tempest-space-1);
    margin: 0;
    padding: 0;
    list-style: none;
}

.bubbleRow {
    display: flex;
    align-items: center;
    gap: var(--tempest-space-2);
}

.ownRun .bubbleRow {
    flex-direction: row-reverse;
}

.bubble {
    display: flex;
    flex-direction: column;
    gap: var(--tempest-space-1);
    min-width: 0;
    padding: var(--tempest-space-2) var(--tempest-space-3);
    border: 1px solid var(--tempest-border);
    border-radius: var(--tempest-radius-lg);
    background-color: var(--tempest-surface);
    font-size: var(--tempest-text-sm);
    line-height: var(--tempest-leading-normal);
}

.ownBubble {
    border-color: transparent;
    background-color: var(--tempest-primary-soft);
    color: var(--tempest-primary-on-soft);
}

/*
 * A failed message keeps its text readable and states the failure at the border
 * and in the meta row. Tinting the whole bubble danger-red would make the message
 * itself hard to read — and re-reading it is exactly what somebody does before
 * deciding to retry.
 */
.failedBubble {
    border-color: var(--tempest-danger);
    background-color: var(--tempest-danger-bg);
    color: var(--tempest-text);
}

.body {
    overflow-wrap: anywhere;
    white-space: pre-wrap;
}

/*
 * The timestamp uses the muted token, not the subtle one, and switches to the
 * bubble's own foreground when the bubble is tinted.
 *
 * `--tempest-text-subtle` is solved against the page and surface backgrounds. On
 * `--tempest-primary-soft` it is a different composite, and the real browser
 * measured it below the 4.5:1 text floor — the same trap the syntax tokens hit.
 * Size is what de-emphasises the meta row here; colour still has to pass.
 */
.meta {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--tempest-space-2);
    color: var(--tempest-text-muted);
    font-size: var(--tempest-text-2xs);
    font-variant-numeric: tabular-nums;
}

.ownBubble .meta {
    color: var(--tempest-primary-on-soft);
}

.status {
    display: inline-flex;
    align-items: center;
}

.statusFailed {
    color: var(--tempest-danger);
    font-weight: var(--tempest-weight-bold);
}

/*
 * A bubble row carries more than a bubble now: the actions button sits beside it
 * and the reaction chips hang under it, so the row wraps and the chips are given
 * the whole width to fall onto.
 */
.bubbleRow {
    flex-wrap: wrap;
}

.reactions {
    display: flex;
    flex-basis: 100%;
    flex-wrap: wrap;
    gap: var(--tempest-space-1);
    margin: 0;
    padding: 0;
    list-style: none;
}

.ownRun .reactions {
    justify-content: flex-end;
}

.reaction {
    display: inline-flex;
    align-items: center;
    gap: var(--tempest-space-1);
    padding: 0 var(--tempest-space-2);
    border: 1px solid var(--tempest-border);
    border-radius: var(--tempest-radius-full, 999px);
    background-color: var(--tempest-surface);
    color: var(--tempest-text);
    font: inherit;
    font-size: var(--tempest-text-xs);
    line-height: 1.8;
    cursor: pointer;
}

.reaction:disabled {
    cursor: default;
}

/*
 * Your own reaction is marked by a tinted chip and its own foreground, never by
 * `--tempest-text-subtle` over the tint: that token is solved against the page
 * background and fails the text floor on a tinted surface.
 */
.reactionMine {
    border-color: transparent;
    background-color: var(--tempest-primary-soft);
    color: var(--tempest-primary-on-soft);
    font-weight: var(--tempest-weight-medium);
}

/*
 * The ⋮ button is always in the DOM — on touch there is no hover to reveal it,
 * and a control that only appears on hover is a control a phone does not have.
 * A fine pointer gets it faded until the row is hovered or it is focused.
 */
.actionsButton {
    flex: 0 0 auto;
    padding: 0 var(--tempest-space-1);
    border: none;
    background: none;
    color: var(--tempest-text-muted);
    font: inherit;
    line-height: 1;
    cursor: pointer;
}

@media (hover: hover) and (pointer: fine) {
    .actionsButton {
        opacity: 0;
        transition: var(--tempest-transition-opacity, opacity 120ms ease);
    }

    .bubbleRow:hover .actionsButton,
    .actionsButton:focus-visible {
        opacity: 1;
    }
}

/* The reply stub: a tinted strip with a bar on the side it is anchored to. */
.quote {
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
    padding: var(--tempest-space-1) var(--tempest-space-2);
    border-left: 3px solid var(--tempest-primary);
    border-radius: var(--tempest-radius-sm);
    background-color: var(--tempest-surface-2);
    color: var(--tempest-text);
    font-size: var(--tempest-text-xs);
    text-align: left;
}

.quoteButton {
    border-top: none;
    border-right: none;
    border-bottom: none;
    font: inherit;
    font-size: var(--tempest-text-xs);
    cursor: pointer;
}

/*
 * The author of a quote is text, so it is coloured like text.
 *
 * `--tempest-primary` here measured **4.35:1** against `--tempest-surface-2` in a
 * real browser — below the 4.5:1 floor for a 12px label, and invisible to the
 * jsdom axe sweep, which disables `color-contrast` because nothing is painted.
 * The brand still marks the quote: it is the bar down the side, which is a
 * non-text indicator and only owes 3:1.
 */
.quoteAuthor {
    color: var(--tempest-text);
    font-weight: var(--tempest-weight-medium);
}

.quoteText {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow-wrap: anywhere;
}

/*
 * A revoked quote reads as absent, which is the point: the text of a deleted
 * message must not survive inside the reply to it.
 */
.quoteRevoked {
    color: var(--tempest-text-muted);
    font-style: italic;
}

.tombstone {
    display: flex;
    align-items: center;
    gap: var(--tempest-space-1);
    color: var(--tempest-text-muted);
    font-style: italic;
}

.deletedBubble {
    border-style: dashed;
    background-color: transparent;
}

.edited {
    font-style: italic;
}

/* Read by everyone is the only state that turns the ticks. */
.statusRead {
    color: var(--tempest-info, var(--tempest-primary));
}

.attachments {
    display: flex;
    flex-direction: column;
    gap: var(--tempest-space-1);
    max-width: 320px;
}

/* Holds the row's height while the attachments chunk is still loading. */
.attachmentsFallback {
    min-height: var(--tempest-space-8, 2rem);
}

.imageAttachment {
    padding: 0;
    border: none;
    border-radius: var(--tempest-radius-md);
    background: none;
    line-height: 0;
    cursor: pointer;
}

.imageAttachment img {
    max-width: 100%;
    max-height: 260px;
    border-radius: var(--tempest-radius-md);
    object-fit: cover;
}

.videoAttachment {
    max-width: 100%;
    border-radius: var(--tempest-radius-md);
}

.voiceAttachment {
    display: flex;
    flex-direction: column;
    gap: var(--tempest-space-1);
}

/*
 * The waveform is the difference between a voice note and a grey rectangle: it
 * is what tells somebody whether this is a word or a two-minute monologue.
 */
.waveform {
    display: flex;
    align-items: flex-end;
    gap: 2px;
    height: 28px;
}

.waveform span {
    flex: 1 1 auto;
    min-width: 2px;
    border-radius: 1px;
    background-color: var(--tempest-primary);
    opacity: 0.7;
}

.fileAttachment {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: var(--tempest-space-2);
    border: 1px solid var(--tempest-border);
    border-radius: var(--tempest-radius-md);
    color: var(--tempest-text);
    text-decoration: none;
}

.fileName {
    overflow-wrap: anywhere;
    font-weight: var(--tempest-weight-medium);
}

.fileMeta {
    color: var(--tempest-text-muted);
    font-size: var(--tempest-text-2xs);
    font-variant-numeric: tabular-nums;
}

.retry {
    padding: 0;
    border: none;
    background: none;
    color: var(--tempest-danger);
    font: inherit;
    font-size: var(--tempest-text-xs);
    text-decoration: underline;
    cursor: pointer;
}

.typing {
    flex: 0 0 auto;
    margin: 0;
    padding: 0 var(--tempest-space-4) var(--tempest-space-2);
    color: var(--tempest-text-subtle);
    font-size: var(--tempest-text-xs);
}

.composer {
    display: flex;
    flex: 0 0 auto;
    align-items: flex-end;
    gap: var(--tempest-space-2);
    padding: var(--tempest-space-3) var(--tempest-space-4);
    border-top: 1px solid var(--tempest-border);
}

.field {
    flex: 1 1 auto;
    min-width: 0;
    padding: var(--tempest-space-2) var(--tempest-space-3);
    border: 1px solid var(--tempest-border);
    border-radius: var(--tempest-radius-md);
    background-color: var(--tempest-bg);
    color: inherit;
    font: inherit;
    font-size: var(--tempest-text-sm);
    line-height: var(--tempest-leading-normal);
    resize: none;
}

.field:focus-visible {
    outline: 2px solid var(--tempest-primary);
    outline-offset: -1px;
}

.field:disabled {
    background-color: var(--tempest-surface-2);
    color: var(--tempest-text-subtle);
    cursor: not-allowed;
}

.composerActions {
    display: flex;
    align-items: center;
    gap: var(--tempest-space-2);
}

.send {
    padding: var(--tempest-space-2) var(--tempest-space-4);
    border: none;
    border-radius: var(--tempest-radius-md);
    background-color: var(--tempest-primary);
    color: var(--tempest-primary-foreground);
    font: inherit;
    font-size: var(--tempest-text-sm);
    font-weight: var(--tempest-weight-medium);
    cursor: pointer;
}

.send:disabled {
    background-color: var(--tempest-surface-3);
    color: var(--tempest-text-subtle);
    cursor: not-allowed;
}

@media (max-width: 480px) {
    .runBody {
        max-width: 88%;
    }
}
